import {
  Box,
  TextField,
  TextFieldProps as MuiTextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, ReactElement, ReactNode } from 'react';

import InfoCircleGrey from '@/assets/svg/ui-elements/info-circle-outline-gray.svg';

type CustomTextFieldProps = MuiTextFieldProps & {
  infoTooltip?: string;
  tooltipIcon?: ReactElement;
  labelColor?: 'text.primary' | 'text.secondary' | 'error.main' | string;
  bigLabel?: boolean;
  noLabel?: boolean;
  changed?: boolean;
  changedColor?: string;
};

export const CustomTextField: FC<CustomTextFieldProps> = ({
  labelColor = 'text.primary',
  label,
  infoTooltip,
  required,
  bigLabel,
  noLabel = false,
  changed = false,
  changedColor = '#F5841F',
  sx,
  tooltipIcon,
  ...props
}) => {
  const renderLabel = (): ReactNode => {
    return (
      <Box display="flex" alignItems="center" gap="6px">
        <Typography variant={bigLabel ? 'body2' : 'body4'} component="span" color={labelColor}>
          {label}
          {required && (
            <Box component="span" color="error.main" ml="2px">
              *
            </Box>
          )}
        </Typography>
        {infoTooltip && (
          <Tooltip
            slotProps={{ tooltip: { sx: { background: '#111D24' } } }}
            title={<Typography variant="body3">{infoTooltip}</Typography>}
            placement="top"
          >
            <Box display="flex" alignItems="center">
              {tooltipIcon ? tooltipIcon : <InfoCircleGrey />}
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <TextField
      {...props}
      label={noLabel ? null : renderLabel()}
      InputLabelProps={{ shrink: true, sx: { transform: 'none' } }}
      slotProps={{
        ...props.slotProps,
        input: { ...props.slotProps?.input, disableUnderline: true },
      }}
      sx={[
        (theme) => ({
          '&': {
            width: '100%',
          },
          ...(changed
            ? {
                [`& .MuiInputBase-root`]: {
                  borderColor: `${changedColor} !important`,
                },
              }
            : {}),
          '& .MuiInputBase-inputMultiline': {
            overflowY: 'auto',
            paddingRight: '8px',
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.palette.primary.main} transparent`,
          },
          '& input:-webkit-autofill': {
            WebkitTextFillColor: theme.palette.text.primary,
            transition: 'background-color 5000s ease-in-out 0s',
            caretColor: '#fff',
          },
          '& input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitTextFillColor: theme.palette.text.primary,
            caretColor: '#fff',
          },
          '& .MuiFormHelperText-root': {
            marginBottom: '-10px',
          },
        }),
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
      variant="standard"
    />
  );
};
